import {
  LEGACY_TIERS,
  PERMISSION_GROUPS,
  PERMISSIONS,
  SYSTEM_ROLES,
  type IPermissionDef,
  type LegacyTier,
  type PermissionGroupId,
  type PermissionKey,
  type Role,
} from '@nexa/auth';

// Pure grant logic for the access screens — what an actor may grant, and who grants what.
// The guardrails are the BFF's (brief §4.19, `operator-bff/src/modules/access/
// access.guardrails.ts`); the UI mirrors them only to disable and explain BEFORE the
// request, never instead of the server.

/** The catalog laid out by its 16 groups (§4.1–§4.16), catalog order inside each. */
export interface IPermissionGroupView {
  id: PermissionGroupId;
  permissions: readonly IPermissionDef[];
}

export const CATALOG_BY_GROUP: readonly IPermissionGroupView[] = PERMISSION_GROUPS.map((g) => ({
  id: g.id,
  permissions: PERMISSIONS.filter((p) => p.group === g.id),
}));

/** Precedence of a legacy tier: 0 is the highest (`admin`); unknown sorts past the end. */
export function tierRank(tier: LegacyTier | Role | null | undefined): number {
  const at = tier ? (LEGACY_TIERS as readonly string[]).indexOf(tier) : -1;
  return at < 0 ? LEGACY_TIERS.length : at;
}

/**
 * Would a role of `tier` lift its holders above the actor's own derived legacy tier
 * (M1, brief §8.4)? An actor with no legacy tier can grant no tier at all.
 */
export function tierEscalates(actorTier: Role | null, tier: LegacyTier): boolean {
  if (actorTier === null) return true;
  return tierRank(tier) < tierRank(actorTier);
}

/** The "Nivel base (compatibilidad)" values an actor may choose: at or below their own. */
export function grantableTiers(actorTier: Role | null): LegacyTier[] {
  return LEGACY_TIERS.filter((tier) => !tierEscalates(actorTier, tier));
}

export type RoleGrantBlock =
  | { reason: 'permissions'; missing: PermissionKey[] }
  | { reason: 'tier' }
  | null;

/**
 * Why the actor cannot GRANT this role (`null` = they can): it carries a permission they
 * do not hold, or its base tier is above theirs. Removing a role is never blocked here.
 */
export function roleGrantBlock(
  role: { permissions: readonly PermissionKey[]; baseTier: LegacyTier },
  held: ReadonlySet<PermissionKey>,
  actorTier: Role | null,
): RoleGrantBlock {
  const missing = role.permissions.filter((p) => !held.has(p));
  if (missing.length > 0) return { reason: 'permissions', missing };
  if (tierEscalates(actorTier, role.baseTier)) return { reason: 'tier' };
  return null;
}

export interface IGrantingRole {
  key: string;
  name: string;
  system: boolean;
}

export interface IRoleDefinition extends IGrantingRole {
  permissions: readonly PermissionKey[];
}

/**
 * Effective permissions of `roleKeys` (the union), each with the roles that grant it —
 * "otorgado por". Role keys with no known definition contribute nothing.
 */
export function grantsOf(
  roleKeys: readonly string[],
  roles: ReadonlyMap<string, IRoleDefinition>,
): Map<PermissionKey, IGrantingRole[]> {
  const out = new Map<PermissionKey, IGrantingRole[]>();
  for (const key of roleKeys) {
    const role = roles.get(key);
    if (!role) continue;
    for (const p of role.permissions) {
      const by = out.get(p) ?? [];
      by.push({ key: role.key, name: role.name, system: role.system });
      out.set(p, by);
    }
  }
  return out;
}

/** The system roles as definitions — what every console knows without the roles API. */
export const SYSTEM_ROLE_DEFINITIONS: ReadonlyMap<string, IRoleDefinition> = new Map(
  SYSTEM_ROLES.map((r) => [
    r.key,
    { key: r.key, name: r.name, system: true, permissions: r.permissions },
  ]),
);

export function byKey<T extends { key: string }>(items: readonly T[]): Map<string, T> {
  return new Map(items.map((i) => [i.key, i]));
}
